import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { properties } from "../config/ClientConfigurations.js";

class ApiService {
  isTokenOk() {
    const token = localStorage.getItem("token");
    return token && !this.isTokenExpired(token);
  }

  getAccessToken() {
    const token = localStorage.getItem("token");
    if (token && !this.isTokenExpired(token)) {
      return token;
    }

    console.error("Token does not exist or is expired");
    this.logout();
  }

  isSuperAdmin() {
    const accessLevel = localStorage.getItem("accessLevel");
    return accessLevel && accessLevel === "300";
  }

  isReseller() {
    const accessLevel = localStorage.getItem("accessLevel");
    return accessLevel && accessLevel === "200";
  }

  isBackoffice() {
    const accessLevel = localStorage.getItem("accessLevel");
    return accessLevel && accessLevel === "75";
  }

  getId() {
    const id = localStorage.getItem("id");
    if (id) return id;
    throw new Error("Id does not exist in local storage");
  }

  getUsername() {
    const username = localStorage.getItem("username");
    if (username) {
      return username;
    }

    throw new Error("Username does not exist in local storage");
  }

  getFullName() {
    const fullName = localStorage.getItem("fullName");
    const username = localStorage.getItem("username");
    if (fullName) {
      return fullName;
    } else {
      return username;
    }
  }

  getAccessLevel() {
    const accessLevel = localStorage.getItem("accessLevel");
    if (accessLevel) {
      return accessLevel;
    }

    throw new Error("Access Level does not exist in local storage");
  }

  async getMe() {
    return this.get("api/v1/users/me");
  }

  async topUp(id, credits) {
    return this.post(`api/v1/users/${id}/top-up`, { credits: credits });
  }

  async getCredits() {
    const user = await this.getMe();
    return user.credits;
  }

  async login(username, password) {
    try {
      const tokenResponse = await axios.post(`${properties.apiUrl}/auth/token`, { username: username, password: password });

      if (tokenResponse && tokenResponse.data) {
        const accessToken = tokenResponse.data.accessToken;
        // console.log(accessToken);
        const claims = this.getClaims(accessToken);
        // console.log(claims);
        if (claims) {
          const id = claims["user_id"];
          const username = claims["user_username"];
          const fullName = claims["user_fullname"];
          const accessLevel = claims["user_access_level"];
          if (id && username && password) {
            localStorage.setItem("token", accessToken);
            localStorage.setItem("id", id);
            localStorage.setItem("username", username);
            localStorage.setItem("fullName", fullName);
            localStorage.setItem("accessLevel", accessLevel);
          } else {
            throw new Error("Either id, username or access level claims are not provided in jwt.");
          }
        } else {
          throw new Error("Failed to decode jwt token.");
        }
      }
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message);
      }

      throw error;
    }
  }

  logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("id");
    localStorage.removeItem("username");
    localStorage.removeItem("fullName");
    localStorage.removeItem("accessLevel");
    window.location.href = "/auth/login-page";
  }

  getClaims(token) {
    if (token) {
      try {
        return jwtDecode(token);
      } catch (error) {
        console.error("Error decoding token:", error);
        return null;
      }
    } else {
      return null;
    }
  }

  isTokenExpired(token) {
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decodedToken.exp < currentTime;
    } catch (error) {
      console.error("Error decoding token:", error);
      return true; // Treat decoding errors as token expired
    }
  }

  async get(path, queryParams) {
    return this.makeRequest(path, "GET", null, undefined, queryParams);
  }

  async post(path, requestBody) {
    return this.makeRequest(path, "POST", null, requestBody);
  }

  async put(path, requestBody) {
    return this.makeRequest(path, "PUT", null, requestBody);
  }

  async postJson(path, requestBody) {
    return this.makeRequest(path, "POST", { "Content-Type": "application/json" }, requestBody);
  }

  async putJson(path, requestBody) {
    return this.makeRequest(path, "PUT", { "Content-Type": "application/json" }, requestBody);
  }

  async patch(path, requestBody) {
    return this.makeRequest(path, "PATCH", { "Content-Type": "application/json" }, requestBody);
  }

  async patchNoContent(path, queryParams) {
    return this.makeRequest(path, "PATCH", null, undefined, queryParams);
  }

  delete(path, queryParams) {
    return this.makeRequest(path, "DELETE", null, undefined, queryParams);
  }

  async makeRequest(url, method, headers, requestBody, queryParams) {
    const token = this.getAccessToken();
    if (headers) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      headers = { Authorization: `Bearer ${token}` };
    }
    const config = {
      method: method,
      url: `${properties.apiUrl}/${url}`,
      params: queryParams,
      data: requestBody,
      headers: headers,
    };

    try {
      const response = await axios(config);
      return response.data;
    } catch (error) {
      if (error.response.status === 401 || error.response.status === 403) this.logout();
      console.log(`Failed to make ${method} request to ${url}: ${error.response.data.message}`);
      console.log(error);
      throw new Error(`${error.response.status}: ${error.response.data.message}`);
    }
  }
}

export const apiService = new ApiService();
